import {
  AutoCompleteGenericForm,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import { Box, Grid, Skeleton, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { TConfirmTopMedScheduleForm } from "../confirmTopMedSchedueSchema";
import { useWatch } from "react-hook-form";
import { IPhysician, ISpecialty } from "../../../../../models/topmed";
import { GenericService } from "../../../../../services/genericService.service";
import { useParams } from "react-router-dom";
import { useTopMedSchedule } from "../../../../../store/contexts/TopMedScheduleContext/TopMedScheduleContext";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useExtensionConfigs } from "../../../../../hooks/useExtensionConfigs";
import { Extension } from "../../../../../models/intalled-extensions";

const SPECIALTIES_ROUTE =
  process.env.REACT_APP_APP +
  "/api/v1/TopMed/ElectiveAppointments/ListSpecialties";
const specialtiesService = new GenericService<ISpecialty>(SPECIALTIES_ROUTE);

const PHYSICIANS_ROUTE =
  process.env.REACT_APP_APP +
  "/api/v1/TopMed/ElectiveAppointments/ListPhysicians";
const physiciansService = new GenericService<IPhysician>(PHYSICIANS_ROUTE);

const TRANSLATION_PATH = "page.topmedSchedule.newSchedule.";

export const FirstStep = memo(() => {
  const { beneficiaryId } = useParams();

  const { t } = useTranslation();

  const { control, setValue } = useFormContext<TConfirmTopMedScheduleForm>();

  const { onFetchPhysicians, onLoading, lastBatchRef, onFetchScheduleDates } =
    useTopMedSchedule();

  const [professionals, setProfessionals] = useState<IPhysician[]>([]);

  const [specialties, setSpecialties] = useState<ISpecialty[]>([]);

  const currentSpecialty = useWatch({ name: "specialty", control });

  const currentDate = useWatch({ name: "date", control });

  const { sendRequest, loading } = useFetch(physiciansService.listGeneric);

  const { sendRequest: getSpecialties, loading: loadingSpecialties } = useFetch(
    specialtiesService.listGeneric
  );

  const { fetchExtensionConfig } = useExtensionConfigs();

  const fetchProfessionals = useCallback(async () => {
    if (!currentSpecialty || !currentDate || !beneficiaryId) return;

    const currentDateISO =
      currentDate.toISOString().slice(0, 10) + "T00:00:00Z";
    const specialtyCode = currentSpecialty.code;
    const { data, success } = await sendRequest({
      beneficiaryId,
      date: currentDateISO,
      specialtyIdentifier: specialtyCode,
    });
    if (data && success) {
      setProfessionals(data);
      onFetchPhysicians(data);
    }

    const month = currentDate.getMonth();
    const shouldBatch =
      !lastBatchRef.current ||
      lastBatchRef.current.month !== month ||
      lastBatchRef.current.specialty !== specialtyCode;

    if (shouldBatch) {
      const configRefundBankingDescription = await fetchExtensionConfig(
        Extension.TOPMED_SCHEDULE,
        "FindFullMonth"
      );
      if (configRefundBankingDescription && configRefundBankingDescription.value === "true") {
        onFetchScheduleDates([]);
        lastBatchRef.current = { month, specialty: specialtyCode };

        const year = currentDate.getFullYear();
        const today = currentDate.getDate();
        const lastDay = new Date(year, month + 1, 0).getDate();

        const dateList = Array.from(
          { length: lastDay - today + 1 },
          (_, i) =>
            new Date(Date.UTC(year, month, today + i, 0, 0, 0))
              .toISOString()
              .slice(0, 10) + "T00:00:00Z"
        );

        const requests = dateList.map((dateISO) =>
          sendRequest({
            beneficiaryId,
            date: dateISO,
            specialtyIdentifier: specialtyCode,
          })
        );

        try {
          const responses = await axios.all(requests);
          const datesInSchedules = responses.flatMap((res: any) =>
            res.data
              ? res.data.flatMap((p: any) =>
                  p.schedules.map((s: any) => s.date)
                )
              : []
          );
          const uniqueDates = Array.from(
            new Set(datesInSchedules.map((dt: string) => dt.slice(0, 10)))
          );
          onFetchScheduleDates(uniqueDates);
        } catch (err) {
          console.error("Erro ao montar scheduleDates:", err);
        }
      }
    }
  }, [
    currentSpecialty,
    currentDate,
    beneficiaryId,
    lastBatchRef,
    sendRequest,
    onFetchPhysicians,
    onFetchScheduleDates,
    fetchExtensionConfig,
  ]);
  const params = useMemo(() => {
    return { beneficiaryId };
  }, [beneficiaryId]);

  const fetchSpecialties = useCallback(async () => {
    const { data, success } = await getSpecialties(params);
    if (data && success) {
      setSpecialties(data);
    }
  }, [getSpecialties, params]);

  const specialtyChangeHandler = useCallback(
    (value: ISpecialty | null) => {
      const today = new Date().toISOString().slice(0, 10);

      if (value) {
        if (value.nextAvailableDate) {
          setValue(
            "date",
            new Date(value.nextAvailableDate.slice(0, 10) + "T15:00:00Z")
          );
        } else {
          setValue("date", new Date(today + "T15:00:00Z"));
        }
        fetchSpecialties();
      }
    },
    [setValue, fetchSpecialties]
  );
  useEffect(() => {
    onLoading(loading);
  }, [loading, onLoading]);

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  useEffect(() => {
    fetchSpecialties();
  }, [fetchSpecialties]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.6, -0.05, 0.01, 0.99] }}
      style={{ paddingTop: 5 }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={4}>
          {loadingSpecialties && (
            <Skeleton variant="rounded" sx={{ height: 40 }} />
          )}
          {!loadingSpecialties && (
            <AutoCompleteGenericForm<ISpecialty>
              name="specialty"
              label={t(TRANSLATION_PATH + "specialty")}
              getOptionLabel={(option) => option.description}
              isOptionEqualToValue={(option, value) =>
                option.code === value.code
              }
              size="small"
              autoFocus
              clearIcon={false}
              onValueChange={specialtyChangeHandler}
              options={specialties}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          {loadingSpecialties && (
            <Skeleton variant="rounded" sx={{ height: 40 }} />
          )}
          {!loadingSpecialties && (
            <Tooltip
              title={t(TRANSLATION_PATH + "professional_tooltip")}
              disableHoverListener={!!currentSpecialty}
              disableFocusListener={!!currentSpecialty}
            >
              <Box>
                <AutoCompleteGenericForm<IPhysician>
                  name="professional"
                  label={t(TRANSLATION_PATH + "professional")}
                  disable={!currentSpecialty}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  size="small"
                  options={professionals}
                />
              </Box>
            </Tooltip>
          )}
        </Grid>
      </Grid>
    </motion.div>
  );
});
